import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import AxiosHelper from 'services/AxiosHelper';
import { Localizer } from 'services/Localizer';
import {
	AddAllToCartResponse,
	AddToCartResponse,
	BulkAddToCartResponse,
	CartSummary,
	GetCartResult,
	MoveSavedProductToCartResult,
} from 'services/UserData.Commerce';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import { AddToCartExceptionCode } from '../product-details/types';
import { OrderMessage } from './OrderMessages';
import {
	AddAllToCartProps as AddAllToCartParams,
	AddToCartProps as AddToCartParams,
	BulkAddToCartProps,
	GetSavedProductsResult,
	MoveStatus,
	Order,
	OrderItem,
	OrderRecipient,
} from './types';

export interface UseShoppingCart {
	state: UseShoppingCartState;
	actor: UseShoppingCartActor;
}

export interface UseShoppingCartState {
	order?: Order | null;
	savedProducts?: GetSavedProductsResult | null;
	cartTotalItemCount: number;

	orderIsUpdating: boolean;
	updatingOrderItemId: string | null;
	orderItemBeingRemovedId: string | null;
	updatingSavedProductId: string | null;
	updatingSavedProductPricingLineId: string | null;
	savedProductBeingRemovedId: string | null;
	savedProductBeingRemovedPricingLineId: string | null;

	storedForUndoItem: OrderItem | null;
	storedForUndoItemIndex: {
		recipientId: string;
		itemIndex: number;
	} | null;
	lastMoveStatus: MoveStatus | null;
	isUndoClickedState: boolean;

	errorMessage: string | null;
	error: Error | AxiosError | null;
	errorType: AddToCartExceptionCode | null;
}

export interface UseShoppingCartActor {
	addToCart: (props: AddToCartParams) => Promise<AddToCartResponse | null>;
	bulkAddToCart: (props: BulkAddToCartProps) => Promise<BulkAddToCartResponse | null>;
	addAllFromListToCurrentCart: (props: AddAllToCartParams) => Promise<AddAllToCartResponse | null>;
	addAllFromListToNewCart: (props: AddAllToCartParams) => Promise<BulkAddToCartResponse | null>;
	changeOrderItemQuantity: (itemToUpdate: OrderItem, newQuantity: number) => Promise<void>;
	moveItemToSaved: (itemToMove: OrderItem) => Promise<void>;
	removeOrderItem: (itemToRemove: OrderItem) => Promise<void>;

	addToSavedProducts: (itemToAdd: OrderItem) => Promise<void>;
	moveSavedProductToCart: (
		productToMoveId: string,
		productName: string,
		pricingLineId: string,
		quantity: number,
	) => Promise<void>;
	removeSavedProduct: (
		productToRemoveId: string,
		productToRemoveName: string,
		pricingLineId: string,
	) => Promise<void>;

	setErrorMessage: (errorMessage: string | null) => void;
	setError: (error: Error | AxiosError | null) => void;
	handleUndo: () => Promise<void>;

	updateOrder: (newCart: Order) => void;
	/**
	 * Sets order messages to displayed whenever the server returns order related ajax requests
	 * within any component tgat is using this hook.
	 */
	updateOrderMessages: (orderMessages: OrderMessage[]) => void;
}

export interface UseShoppingCartParams {
	order?: Order | null;
	setOrder?: (order: Order) => void;
	savedProducts?: GetSavedProductsResult | null;
	setSavedProducts?: (savedProducts: GetSavedProductsResult) => void;
	cartSummary?: CartSummary | null;
	setCartSummary?: (cartSummary: CartSummary) => void;
	orderMessages?: OrderMessage[];
	setOrderMessages?: (orderMessages: OrderMessage[]) => void;
}

/**
 * Reusable hook to access ShoppingCart-related endpoints on the server, as well as helping manage
 * the items inside the shopping cart.
 */
export default function useShoppingCart(useShoppingCartParams?: UseShoppingCartParams): UseShoppingCart {
	const params = useShoppingCartParams || {};
	const { order, setOrder, savedProducts, setSavedProducts, setCartSummary, setOrderMessages } = params;

	// to indicate that a cart is being updated after its item has been removed/updated
	const [updatingOrderItemId, setUpdatingOrderItemId] = useState<string | null>(null);

	// to store the id of the order item being removed
	const [orderItemBeingRemovedId, setOrderItemBeingRemovedId] = useState<string | null>(null);

	// total items count in the cart
	const [cartTotalItemCount, setCartTotalItemCount] = useState(0);

	// to store the id and the unit of measure id of the item being moved
	const [updatingSavedProductId, setUpdatingSavedProductId] = useState<string | null>(null);
	const [updatingSavedProductPricingLineId, setUpdatingSavedProductPricingLineId] = useState<string | null>(null);

	// to store the id and the unit of measure id of the item being removed
	const [savedProductBeingRemovedId, setSavedProductBeingRemovedId] = useState<string | null>(null);
	const [savedProductBeingRemovedPricingLineId, setSavedProductBeingRemovedPricingLineId] = useState<string | null>(
		null,
	);

	// stores the last removed item and item index to be able to undo the last move or removal
	const [storedForUndoItem, setStoredForUndoItem] = useState<OrderItem | null>(null);
	const [storedForUndoItemIndex, setStoredForUndoItemIndex] = useState<{
		recipientId: string;
		itemIndex: number;
	} | null>(null);

	// stores what the last action was, such as removing an item from the cart or moving an item to the saved products list
	const [lastMoveStatus, setLastMoveStatus] = useState<MoveStatus | null>(null);

	// stores whether the undo button was clicked, to decide whether to clear the undo state when running a function or not
	let isUndoClicked: boolean = false;
	const [isUndoClickedState, setIsUndoClickedState] = useState(false);

	// stores the error message and error type to display
	const [error, setError] = useState<Error | AxiosError | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [errorType, setErrorType] = useState<AddToCartExceptionCode | null>(null);

	// whether the order is updating
	const orderIsUpdating =
		(orderItemBeingRemovedId ||
			updatingOrderItemId ||
			updatingSavedProductId ||
			updatingSavedProductPricingLineId ||
			savedProductBeingRemovedId ||
			savedProductBeingRemovedPricingLineId) != null || isUndoClickedState;

	const clearUndoState = () => {
		setStoredForUndoItem(null);
		setStoredForUndoItemIndex(null);
		setLastMoveStatus(null);
	};

	const dispatchAddToCartEvent = (result: AddToCartResponse) => {
		dispatchRocEvent('added-to-cart', {
			items: [
				{
					id: result.productId,
					adjustedQuantity: result.adjustedQuantity,
					name: result.name,
					price: result.snapshotSalePrice ? result.snapshotSalePrice : result.snapshotBasePrice,
					sku: result.sku,
					currency: result.snapshotCurrencyISO,
					productGroupId: result.productGroupId,
					selections: result.selections,
				},
			],
		});
	};

	/**
	 * Updates the cart, saved products, and cart summary
	 * @param response
	 * @param cartSummary
	 */
	const updateCartState = (response?: GetCartResult | null) => {
		if (!response) {
			return;
		}

		if (response.cart) {
			if (!setOrder) {
				console.warn('setOrder called but not defined.');
			} else {
				setOrder(response.cart);
			}
		}

		if (response.savedProducts) {
			if (!setSavedProducts) {
				console.warn('setSavedProducts called but not defined.');
			} else {
				setSavedProducts(response.savedProducts);
			}
		}

		if (response.cartSummary) {
			if (!setCartSummary) {
				console.warn('setCartSummary called but not defined.');
			} else {
				setCartSummary(response.cartSummary);
			}
		}

		updateOrderMessages(response.orderMessages);
	};

	// this runs when the component is mounted and every time the order changes because we want
	// to recalculate cart total item count
	useEffect(() => {
		if (!order) {
			setCartTotalItemCount(0);
		} else {
			const itemsCount = order.recipients.reduce((itemsQty: number, orderRecipient: OrderRecipient) => {
				itemsQty += orderRecipient.items.reduce((recipientQty: number, orderItem: OrderItem) => {
					recipientQty += orderItem.quantity;
					return recipientQty;
				}, 0);
				return itemsQty;
			}, 0);

			setCartTotalItemCount(itemsCount);
		}
		clearUndoState();
	}, [order]);

	//#region Shopping Cart

	/**
	 * Adds the last removed item to the cart
	 */
	const addToCart = async (props: AddToCartParams) => {
		setErrorMessage(null);
		setError(null);

		if (!isUndoClicked) {
			clearUndoState();
		}

		setUpdatingOrderItemId(props.itemId);

		try {
			const response = await AxiosHelper.post<AddToCartResponse>('/ajax/cart', {
				productId: props.itemId,
				quantity: props.quantity,
				productPricingLineId: props.productPricingLineId,
				savedProductsPageNumber: savedProducts?.pagination?.pageNumber || 1,
				savedProductsPageSize: savedProducts?.pagination?.pageSize || 1000000,
			});

			updateCartState(response.data);

			if (response.data) {
				dispatchAddToCartEvent(response.data);
			}

			return response.data;
		} catch (error) {
			clearUndoState();
			setError(error);

			console.warn('Error when undoing a remove from cart action', error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data) {
				if (axiosError.response.data.message) {
					setErrorMessage(axiosError.response.data.message);
				}
				if (axiosError.response.data.exceptionCode) {
					setErrorType(axiosError.response.data.exceptionCode);
				}
			} else {
				throw error;
			}

			return null;
		} finally {
			setUpdatingOrderItemId(null);
		}
	};

	/**
	 * Adds multiple items to cart
	 */
	const bulkAddToCart = async (props: BulkAddToCartProps) => {
		setErrorMessage(null);
		setError(null);

		if (!isUndoClicked) {
			clearUndoState();
		}

		props.items.forEach((element) => {
			setUpdatingOrderItemId(element.itemId);
		});

		try {
			const request = {
				productInfo: props.items.map((item, index) => {
					return {
						productId: item.itemId,
						productPricingLineId: item.productPricingLineId,
						quantity: item.quantity,
						index: index,
					};
				}),
			};
			const response = await AxiosHelper.post<BulkAddToCartResponse>('/ajax/cart/bulk', request);

			updateCartState(response.data);

			if (response.data.processedItems && response.data.processedItems.length > 0) {
				dispatchRocEvent('added-to-cart', {
					items: response.data.processedItems.map((item) => {
						return {
							adjustedQuantity: item.adjustedQuantity,
							id: item.productId,
							name: item.name,
							sku: item.sku,
							price: item.snapshotSalePrice ? item.snapshotSalePrice : item.snapshotBasePrice,
							currency: item.snapshotCurrencyISO,
							productGroupId: item.productGroupId,
							selections: item.selections,
						};
					}),
				});
			}
			return response.data;
		} catch (error) {
			clearUndoState();
			setError(error);

			console.warn('Error when undoing a remove from cart action', error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data) {
				if (axiosError.response.data.message) {
					setErrorMessage(axiosError.response.data.message);
				}
				if (axiosError.response.data.exceptionCode) {
					setErrorType(axiosError.response.data.exceptionCode);
				}
			} else {
				throw error;
			}

			return null;
		} finally {
			setUpdatingOrderItemId(null);
		}
	};

	/**
	 * Add all items from the specified shopping list to the cart
	 */
	const addAllFromListToCurrentCart = async (props: AddAllToCartParams) => {
		setErrorMessage(null);
		setError(null);

		try {
			const response = await AxiosHelper.post<AddAllToCartResponse>(
				`/ajax/shopping-lists/${props.shoppingListId}/to-cart`,
				{
					displayedItemsCount: 3,
					shoppingListProductIds: props.selectedShoppingListProductIds,
				},
			);

			return response.data;
		} catch (error) {
			clearUndoState();
			setError(error);

			console.warn('Error when adding all products to cart', error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data) {
				if (axiosError.response.data.message) {
					setErrorMessage(axiosError.response.data.message);
				}
				if (axiosError.response.data.exceptionCode) {
					setErrorType(axiosError.response.data.exceptionCode);
				}
			} else {
				throw error;
			}

			return null;
		}
	};

	/**
	 * Add all items from the specified shopping list to the new cart
	 */
	const addAllFromListToNewCart = async (props: AddAllToCartParams) => {
		setErrorMessage(null);
		setError(null);

		try {
			const response = await AxiosHelper.post<BulkAddToCartResponse>(
				`/ajax/shopping-lists/${props.shoppingListId}/to-new-cart`,
				{
					displayedItemsCount: 3,
					shoppingListProductIds: props.selectedShoppingListProductIds,
				},
			);

			return response.data;
		} catch (error) {
			clearUndoState();
			setError(error);

			console.warn('Error when adding all products to cart', error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data) {
				if (axiosError.response.data.message) {
					setErrorMessage(axiosError.response.data.message);
				}
				if (axiosError.response.data.exceptionCode) {
					setErrorType(axiosError.response.data.exceptionCode);
				}
			} else {
				throw error;
			}

			return null;
		}
	};

	/**
	 * Moves a product from the shopping cart to the user's saved products list
	 * @param itemToMove
	 */
	const moveItemToSaved = async (itemToMove: OrderItem) => {
		if (!setSavedProducts || !setOrder || !order) {
			console.warn(
				'Saved product move to cart requested with one or more of the following undefined:\n' +
					'setSavedProducts, setOrder, order',
			);
			return;
		}

		setErrorMessage(null);
		setError(null);

		if (!isUndoClicked) {
			clearUndoState();
		}

		setUpdatingOrderItemId(itemToMove.id);

		try {
			if (!order || !order.recipients) {
				return;
			}

			const response = await AxiosHelper.post<GetCartResult>('/ajax/saved-products/from-cart', {
				productId: itemToMove.product.id,
				itemId: itemToMove.id,
				productPricingLineId: itemToMove.pricingLine.id,
				quantity: itemToMove.quantity,
				savedProductsPageNumber:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageNumber) || 1,
				savedProductsPageSize:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageSize) || 1000000,
			});

			// Find and save the moved item index
			order.recipients.map((recipient) => {
				setStoredForUndoItemIndex({
					recipientId: recipient.id,
					itemIndex: recipient.items.indexOf(itemToMove),
				});
			});

			updateCartState(response.data);
			setStoredForUndoItem(itemToMove);
			setLastMoveStatus(MoveStatus.MovedToSaved);
		} catch (error) {
			clearUndoState();

			setError(error);

			console.warn('Error when saving order item for later', error);

			const axiosError = error as AxiosError;
			if (axiosError && axiosError.response && axiosError.response.data && axiosError.response.data.message) {
				setErrorMessage(axiosError.response.data.message);
			} else {
				setErrorMessage(Localizer('ProblemWhenSavingItem', itemToMove.name));
			}
		} finally {
			setUpdatingOrderItemId(null);
		}
	};

	/**
	 * Updates the quantity of the requested item with the new one
	 * @param itemToUpdate
	 * @param newQuantity
	 */
	const changeOrderItemQuantity = async (itemToUpdate: OrderItem, newQuantity: number) => {
		if (!setOrder) {
			console.warn('Item order quantity change requested with no setOrder function defined.');
			return;
		}

		setErrorMessage(null);
		setError(null);

		if (!isUndoClicked) {
			clearUndoState();
		}

		setUpdatingOrderItemId(itemToUpdate.id);

		try {
			if (!order) {
				return;
			}

			const response = await AxiosHelper.post<GetCartResult>('/ajax/cart/update', {
				itemId: itemToUpdate.id,
				quantity: newQuantity,
				savedProductsPageNumber:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageNumber) || 1,
				savedProductsPageSize:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageSize) || 1000000,
			});

			updateCartState(response.data);
		} catch (error) {
			console.warn(`Error when updating ${itemToUpdate.name}' quantity`, error);

			setError(error);
			const axiosError = error as AxiosError;
			if (axiosError && axiosError.response && axiosError.response.data && axiosError.response.data.message) {
				setErrorMessage(axiosError.response.data.message);
			} else {
				setErrorMessage(Localizer('ProblemWhenUpdatingItem', itemToUpdate.name));
			}
		} finally {
			setUpdatingOrderItemId(null);
			clearUndoState();
		}
	};

	/**
	 * Removes the requested item from the cart
	 * @param itemToRemove
	 */
	const removeOrderItem = async (itemToRemove: OrderItem) => {
		if (!setOrder || !order) {
			console.warn('Order item removal requested with no order or setOrder defined.');
			return;
		}

		setErrorMessage(null);
		setError(null);

		if (!isUndoClicked) {
			clearUndoState();
		}

		setOrderItemBeingRemovedId(itemToRemove.id);

		try {
			// Find and save the moved item index
			order.recipients.map((recipient) => {
				setStoredForUndoItemIndex({
					recipientId: recipient.id,
					itemIndex: recipient.items.indexOf(itemToRemove),
				});
			});

			const response = await AxiosHelper.post('/ajax/cart/remove', {
				itemId: itemToRemove.id,
				savedProductsPageNumber:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageNumber) || 1,
				savedProductsPageSize:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageSize) || 1000000,
			});

			updateCartState(response.data);
			setStoredForUndoItem(itemToRemove);
			setLastMoveStatus(MoveStatus.RemovedFromCart);
		} catch (error) {
			clearUndoState();

			setErrorMessage(Localizer('ProblemWhenRemovingItem', itemToRemove.name));
			setError(error);
		} finally {
			setOrderItemBeingRemovedId(null);
		}
	};
	//#endregion

	//#region Save For Later

	/**
	 * Handles adding an item to a saved products list
	 * @param itemToAdd
	 */
	const addToSavedProducts = async (itemToAdd: OrderItem) => {
		if (!setSavedProducts) {
			console.warn('Add to saved products requested with no setSavedProducts defined.');
			return;
		}

		setErrorMessage(null);
		setError(null);

		if (!isUndoClicked) {
			clearUndoState();
		}

		setUpdatingSavedProductId(itemToAdd.product.id);

		try {
			// Don't need to set undo info here as there is no way to run this function unless you are undoing a removal
			const response = await AxiosHelper.post<GetCartResult>('/ajax/saved-products/save-for-later', {
				productId: itemToAdd.product.id,
				productPricingLineId: itemToAdd.pricingLine.id,
				quantity: itemToAdd.quantity,
				savedProductsPageNumber:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageNumber) || 1,
				savedProductsPageSize:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageSize) || 1000000,
			});

			updateCartState(response.data);
		} catch (error) {
			clearUndoState();
			setError(error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data && axiosError.response.data.message) {
				setErrorMessage(axiosError.response.data.message);
			} else {
				setErrorMessage(Localizer('ProblemWhenSavingItem', itemToAdd.name));
			}
		} finally {
			setUpdatingSavedProductId(null);
		}
	};

	/**
	 * Moves a user's saved product to the shopping cart
	 * @param productToMoveId
	 * @param productName
	 * @param productPricingLineId
	 * @param quantity
	 */
	const moveSavedProductToCart = async (
		productToMoveId: string,
		productName: string,
		productPricingLineId: string,
		quantity: number,
	) => {
		if (!setSavedProducts || !setOrder || !savedProducts) {
			console.warn(
				'Saved product move to cart requested with one or more of the following undefined:\n' +
					'setSavedProducts, setOrder, savedProducts.',
			);
			return;
		}

		setErrorMessage(null);
		setError(error);

		if (!isUndoClicked) {
			clearUndoState();
		}

		setUpdatingSavedProductId(productToMoveId);
		setUpdatingSavedProductPricingLineId(productPricingLineId);

		try {
			if (!savedProducts || !savedProducts.results) {
				return;
			}

			// find the index of the moved item in the saved products list
			const savedProductIndex = savedProducts.results.findIndex(
				(product) => product.productId === productToMoveId,
			);

			const response = await AxiosHelper.post<MoveSavedProductToCartResult>('/ajax/saved-products/to-cart', {
				productId: productToMoveId,
				quantity,
				productPricingLineId: productPricingLineId,
				savedProductsPageNumber:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageNumber) || 1,
				savedProductsPageSize:
					(savedProducts && savedProducts.pagination && savedProducts.pagination.pageSize) || 1000000,
			});

			updateCartState(response.data);

			if (response.data.addToOrderResult) {
				dispatchAddToCartEvent(response.data.addToOrderResult);
			}

			// Find and save the moved item
			const recipients = response.data.cart.recipients;

			for (const recipient of recipients) {
				const lastMovedItem = recipient.items.find(
					(item) => item.product.id === productToMoveId && item.pricingLine.id === productPricingLineId,
				);
				if (lastMovedItem) {
					setStoredForUndoItem({ ...lastMovedItem, quantity });
					setStoredForUndoItemIndex({ recipientId: recipient.id, itemIndex: savedProductIndex });
					console.log('Found last moved item');
					break;
				}
			}

			setLastMoveStatus(MoveStatus.MovedToCart);
		} catch (error) {
			clearUndoState();
			setError(error);

			console.warn('Error when moving the saved product to the cart.', error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data && axiosError.response.data.message) {
				setErrorMessage(axiosError.response.data.message);
			} else {
				setErrorMessage(Localizer('ProblemWhenMovingSavedItemToCart', productName));
			}
		} finally {
			setUpdatingSavedProductId(null);
			setUpdatingSavedProductPricingLineId(null);
		}
	};

	/**
	 * Remove a user's saved product from their saved products list
	 * @param productToRemoveId
	 * @param productPricingLineId
	 */
	const removeSavedProduct = async (
		productToRemoveId: string,
		productToRemoveName: string,
		productPricingLineId: string,
	) => {
		if (!setSavedProducts || !savedProducts) {
			console.warn('Saved product removal requested with savedProducts or setSavedProducts undefined');
			return;
		}

		setErrorMessage(null);
		setError(null);

		if (!isUndoClicked) {
			clearUndoState();
		}

		setSavedProductBeingRemovedId(productToRemoveId);
		setSavedProductBeingRemovedPricingLineId(productPricingLineId);

		try {
			if (!savedProducts.results) {
				return;
			}

			const savedProductIndex = savedProducts.results.findIndex(
				(product) => product.productId === productToRemoveId,
			);
			const savedProduct = savedProducts.results[savedProductIndex];
			const response = await AxiosHelper.delete<GetCartResult>(
				'/ajax/saved-products/' +
					productToRemoveId +
					'/' +
					productPricingLineId +
					'?savedProductsPageNumber=' +
					savedProducts.pagination.pageNumber.toString() +
					'&savedProductsPageSize=' +
					savedProducts.pagination.pageSize.toString(),
			);

			if (savedProduct) {
				// Convert the SavedProduct into an OrderItem
				const orderItemFromSaved: OrderItem = {
					...savedProduct,
					product: { ...savedProduct, id: savedProduct.productId },
					totalPrice: savedProduct.totalPrice,
					unitPrice: savedProduct.unitPrice,
					snapshotMinimumOrderQuantity: null,
				};

				setStoredForUndoItem(orderItemFromSaved);
				setStoredForUndoItemIndex({ recipientId: '', itemIndex: savedProductIndex });
			}

			updateCartState(response.data);
			setLastMoveStatus(MoveStatus.RemovedFromSaved);
		} catch (error) {
			clearUndoState();
			setError(error);

			console.warn('Error when removing the saved product.', error);

			setErrorMessage(Localizer('ProblemWhenRemovingSavedItem', productToRemoveName));
		} finally {
			setSavedProductBeingRemovedId(null);
			setSavedProductBeingRemovedPricingLineId(null);
		}
	};
	//#endregion

	/**
	 * Handle what happens when the Undo button is pressed
	 */
	const handleUndo = async () => {
		setErrorMessage(null);
		setError(null);

		if (!lastMoveStatus == null || !storedForUndoItem) {
			return;
		}

		isUndoClicked = true;
		setIsUndoClickedState(true);

		try {
			switch (lastMoveStatus) {
				case MoveStatus.RemovedFromCart:
					await addToCart({
						itemId: storedForUndoItem.product.id,
						quantity: storedForUndoItem.quantity,
						productPricingLineId: storedForUndoItem.pricingLine.id,
					});
					break;

				case MoveStatus.MovedToCart:
					await moveItemToSaved(storedForUndoItem);
					break;

				case MoveStatus.MovedToSaved:
					await moveSavedProductToCart(
						storedForUndoItem.product.id,
						storedForUndoItem.name,
						storedForUndoItem.pricingLine.id,
						storedForUndoItem.quantity,
					);
					break;

				case MoveStatus.RemovedFromSaved:
					await addToSavedProducts(storedForUndoItem);
					break;
			}
		} finally {
			isUndoClicked = false;
			setIsUndoClickedState(false);
			clearUndoState();
		}
	};

	/**
	 * Handles updating order
	 * @param newCart
	 */
	const updateOrder = (newCart: Order) => {
		if (setOrder) {
			setOrder(newCart);
		}
	};

	/**
	 * Handles updating order messages
	 * @param orderMessages Updated order messages
	 */
	const updateOrderMessages = (orderMessages: OrderMessage[]) => {
		if (!setOrderMessages) {
			console.warn('setOrderMessages called but not defined.');
		} else {
			setOrderMessages(orderMessages);
		}
	};

	return {
		state: {
			orderIsUpdating,
			order: useShoppingCartParams ? useShoppingCartParams.order : null,
			savedProducts: useShoppingCartParams ? useShoppingCartParams.savedProducts : null,
			cartTotalItemCount,

			updatingOrderItemId,
			orderItemBeingRemovedId,
			updatingSavedProductId,
			updatingSavedProductPricingLineId,
			savedProductBeingRemovedId,
			savedProductBeingRemovedPricingLineId,

			storedForUndoItem,
			storedForUndoItemIndex,
			lastMoveStatus,
			isUndoClickedState,

			error,
			errorMessage,
			errorType,
		},
		actor: {
			addToCart,
			bulkAddToCart,
			addAllFromListToCurrentCart,
			addAllFromListToNewCart,
			changeOrderItemQuantity,
			moveItemToSaved,
			removeOrderItem,

			addToSavedProducts,
			moveSavedProductToCart,
			removeSavedProduct,

			handleUndo,
			setErrorMessage,
			setError,

			updateOrder,
			updateOrderMessages,
		},
	};
}
